import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Conversations from 'components/organisms/smsDashboard/conversations';
import Chat from 'components/organisms/smsDashboard/chat';
import { isMobile } from 'utils/deviceHelper';
import Sidebar from 'components/organisms/smsDashboard/sidebar';
import SidebarUnregistered from 'components/organisms/smsDashboard/sidebar/sidebarUnregistered';
import { useNewMessageBroadcast } from 'infrastructure/hooks/smsDashboard/useNewMessageBroadcast';
import { useUpdateConversationState } from 'infrastructure/hooks/smsDashboard/useUpdateConversationState';
import { useTryUpdateMessageStatus } from 'infrastructure/hooks/smsDashboard/useTryUpdateMessageStatus';
import { useMarkConversation } from 'infrastructure/hooks/smsDashboard/useMarkConversation';
import { CONVERSATION_TYPES } from 'store/smsDashboard';
import { useSmsDashboardSocket } from 'infrastructure/hooks/smsDashboard/useSmsDashboardSocket';
import { useDebouncedEffect } from 'infrastructure/hooks/utils/use-debounced-effect';
import { useAssignConversation } from 'infrastructure/hooks/smsDashboard/useAssignConversation';
import { useUpdateAssignConversationState } from 'infrastructure/hooks/smsDashboard/useUpdateAssignConversationState';
import useUser from 'utils/useUser';
import { differenceInMinutes } from 'date-fns';
import { parseISOorDate } from 'infrastructure/functions/parse-iso-or-date';

import s from './styles.module.scss';

import type { IConversation } from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';
import type { ISocketData as INewMessageSocketData } from 'infrastructure/hooks/smsDashboard/useNewMessageBroadcast';

const SmsDashboard = () => {
  const user = useUser();
  const { currentConversation, conversationType, filters } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();
  const lastConversationRef = useRef<IConversation | null>(null);

  const [scrollToMessage, setScrollToMessage] = useState();

  const isUnreadFilter = filters.read === 'false';
  const isUnreadAllFilter = filters.read === '';

  const device = useSelector((state: RootState) => state.user.deviceType);
  const deviceIsMobile = isMobile(device);

  const { newMessageBroadcast } = useNewMessageBroadcast();
  const { updateConversationState } = useUpdateConversationState();
  const { updateAssignConversationState } = useUpdateAssignConversationState();
  const { tryUpdateMessageStatus } = useTryUpdateMessageStatus();
  const { markConversation } = useMarkConversation({});
  const { assignConversation, assignConversationAsync } =
    useAssignConversation();

  const conversationStateChange = useCallback(
    (data: any) => {
      const isCurrentConversation =
        currentConversation.id === data.conversationId;
      const needCloseConversation = !isCurrentConversation && isUnreadFilter;

      updateConversationState(
        {
          conversationId: Number(data.conversationId),
          read: data.read,
          rpmTotalUnreadCount: data.rpmTotalUnreadCount,
          ccmTotalUnreadCount: data.ccmTotalUnreadCount,
          unregisteredRpmTotalUnreadCount: data.unregisteredRpmTotalUnreadCount,
          unregisteredCcmTotalUnreadCount: data.unregisteredCcmTotalUnreadCount,
        },
        needCloseConversation,
      );
    },
    [currentConversation, isUnreadFilter, updateConversationState],
  );

  const conversationAssignStateChange = useCallback(
    (data: any) => {
      updateAssignConversationState({
        assignedAt: data.assignedAt,
        assignedByFullname: data.assignedByFullname,
        assignedByLogin: data.assignedByLogin,
        conversationId: data.conversationId,
      });
      if (data.conversationId === currentConversation.id) {
        changeCurrentConversation({
          ...currentConversation,
          assignedAt: data.assignedAt,
          assignedByFullname: data.assignedByFullname,
          assignedByLogin: data.assignedByLogin,
        });
      }
    },
    [currentConversation, filters, updateAssignConversationState],
  );

  const doctorMessageStatusChange = useCallback(
    (data: any) => {
      tryUpdateMessageStatus({
        conversationId: Number(data.conversationId),
        messageId: data.messageId,
        status: data.status,
      });
    },
    [tryUpdateMessageStatus],
  );

  const newMessage = useCallback(
    (data: INewMessageSocketData) => {
      const isPatientMessage = data.message.authorType === 'patient';
      const isDoctorMessage = data.message.authorType === 'doctor';
      const isCurrentConversation =
        currentConversation.id === data.conversationId;
      const needMark = isCurrentConversation && isPatientMessage;

      const needAddConversation =
        !isDoctorMessage && (isUnreadFilter || isUnreadAllFilter);

      newMessageBroadcast(data, needAddConversation);

      if (needMark) {
        markConversation({
          conversationId: data.conversationId,
          read: true,
        });
      }
    },
    [
      currentConversation,
      isUnreadFilter,
      isUnreadAllFilter,
      newMessageBroadcast,
      markConversation,
    ],
  );

  useSmsDashboardSocket({
    conversationStateChange,
    conversationAssignStateChange,
    doctorMessageStatusChange,
    newMessage,
  });

  useDebouncedEffect({
    callback: async () => {
      const currentId = currentConversation?.id;
      if (!currentId) return;

      const isAssignee = currentConversation.assignedByLogin === user.guid;

      let less15Minute = false;
      if (currentConversation.assignedAt) {
        const assignedDate = parseISOorDate(currentConversation.assignedAt);
        less15Minute = differenceInMinutes(new Date(), assignedDate) < 15;
      }

      if (!isAssignee && less15Minute) return;

      const shouldSkipAssign = isAssignee && less15Minute;
      if (shouldSkipAssign) return;

      await assignConversationAsync({
        conversationId: currentId,
        action: 'assign',
      });
    },
    value: currentConversation?.id,
    delay: 5000,
  });

  const isCurrentConversationExist = currentConversation.id !== 0;

  const mobilePage = currentConversation ? (
    <Chat />
  ) : (
    <Conversations setScrollToMessage={setScrollToMessage} />
  );

  const isUnregisteredConversationType =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    conversationType === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const isNotUnregisteredConversationType =
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_RPM &&
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_CCM;

  const desktopPage = (
    <>
      <Conversations setScrollToMessage={setScrollToMessage} />
      <Chat scrollToMessage={scrollToMessage} />
      {isCurrentConversationExist && isNotUnregisteredConversationType && (
        <Sidebar />
      )}
      {isCurrentConversationExist && isUnregisteredConversationType && (
        <SidebarUnregistered />
      )}
    </>
  );

  useEffect(() => {
    lastConversationRef.current = currentConversation ?? null;
  }, [currentConversation]);

  useEffect(() => {
    return () => {
      const last = lastConversationRef.current;
      const currentId = last?.id;
      const isAssignee = last?.assignedByLogin === user.guid;

      if (currentId && isAssignee) {
        assignConversation({
          conversationId: currentId,
          action: 'unassign',
        });
      }
    };
  }, []);

  return (
    <div className={`${s['sms-dashboard']} sms-dashboard-padding`}>
      <div className={s['sms-dashboard-body']}>
        {deviceIsMobile ? mobilePage : desktopPage}
      </div>
    </div>
  );
};

export default SmsDashboard;
